import React from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { courseService, CourseEnrollment, Course } from '../../api/courseService';
import Card from '../UI/Card';
import Button from '../UI/Button';
import { theme } from '../../styles/theme';

const CoursesContainer = styled(Card)`
  margin-bottom: ${theme.spacing.lg};
`;

const CoursesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

const CoursesTitle = styled.h2`
  margin: 0;
  font-size: ${theme.fontSizes.large};
`;

const CourseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const CourseItem = styled.div`
  display: flex;
  border-radius: ${theme.radii.small};
  overflow: hidden;
  border: 1px solid ${theme.colors.light};
  
  &:hover {
    box-shadow: ${theme.shadows.small};
  }
`;

const CourseImage = styled.div`
  width: 120px;
  height: 80px;
  background-color: ${theme.colors.light};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CourseInfo = styled.div`
  flex: 1;
  padding: ${theme.spacing.sm};
  display: flex;
  flex-direction: column;
`;

const CourseTitle = styled.h3`
  margin: 0 0 ${theme.spacing.xs} 0;
  font-size: ${theme.fontSizes.medium};
`;

const CourseDescription = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.small};
  color: ${theme.colors.gray};
  flex: 1;
`;

const CourseActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: ${theme.spacing.sm};
  gap: ${theme.spacing.sm};
`;

const LoadingContainer = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${theme.colors.gray};
`;

const ErrorContainer = styled.div`
  padding: ${theme.spacing.md};
  color: ${theme.colors.danger};
  text-align: center;
`;

const EmptyContainer = styled.div`
  padding: ${theme.spacing.md};
  text-align: center;
  
  p {
    margin-bottom: ${theme.spacing.md};
    color: ${theme.colors.gray};
  }
`;

const EnrolledCourses: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['enrolledCourses'],
    queryFn: () => courseService.getEnrolledCourses(),
  });

  if (isLoading) {
    return (
      <CoursesContainer>
        <LoadingContainer>Loading your courses...</LoadingContainer>
      </CoursesContainer>
    );
  }

  if (error) {
    console.error('Error loading enrolled courses:', error);
    return (
      <CoursesContainer>
        <ErrorContainer>Unable to load your enrolled courses. Please try again later.</ErrorContainer>
      </CoursesContainer>
    );
  }

  const enrolledCourses = data?.data?.data || [];
  
  if (enrolledCourses.length === 0) {
    return (
      <CoursesContainer>
        <CoursesHeader>
          <CoursesTitle>My Courses</CoursesTitle>
        </CoursesHeader>
        <EmptyContainer>
          <p>You're not enrolled in any courses yet.</p>
          <Button as={Link} to="/courses">
            Browse Courses
          </Button>
        </EmptyContainer>
      </CoursesContainer>
    );
  }

  return (
    <CoursesContainer>
      <CoursesHeader>
        <CoursesTitle>My Courses</CoursesTitle>
        <Button 
          variant="outline"
          size="small"
          as={Link}
          to="/courses"
        >
          View All Courses
        </Button>
      </CoursesHeader>
      
      <CourseList>
        {enrolledCourses.map((enrollment) => (
          <CourseItem key={enrollment.course_id}>
            <CourseImage>
              <img 
                src={enrollment.thumbnail_url || '/placeholdercourse.png'} 
                alt={enrollment.title || 'Course'} 
              />
            </CourseImage>
            
            <CourseInfo>
              <CourseTitle>{enrollment.title}</CourseTitle>
              <CourseDescription>
                {enrollment.description && enrollment.description.length > 100
                  ? `${enrollment.description.substring(0, 100)}...`
                  : enrollment.description}
              </CourseDescription>
            </CourseInfo>
            
            <CourseActions>
              <Button 
                size="small"
                as={Link}
                to={`/courses/${enrollment.course_id}`}
              >
                Continue
              </Button>
            </CourseActions>
          </CourseItem>
        ))}
      </CourseList>
    </CoursesContainer>
  );
};

export default EnrolledCourses; 